'use strict';

import { Component } from 'react';
import PropTypes from 'prop-types';
import { calculateParticipantServings } from '@utils/servingsCalculations';
import './EditMealServingsModalPrimary.scss';

export default class ParticipantRow extends Component {
    static propTypes = {
        member: PropTypes.object.isRequired,
        index: PropTypes.number.isRequired,
        isParticipating: PropTypes.bool.isRequired,
        editableParticipants: PropTypes.bool.isRequired,
        dishes: PropTypes.array.isRequired,
        contents: PropTypes.object.isRequired,
        isExpanded: PropTypes.bool,
        onToggleExpand: PropTypes.func,
        onToggleParticipant: PropTypes.func.isRequired,
        onChangeParticipantPortion: PropTypes.func.isRequired,
        onChangeDishPortion: PropTypes.func.isRequired,
    };

    static defaultProps = {
        isExpanded: false,
        onToggleExpand: null,
    };

    render() {
        const { member, isParticipating: isEating, editableParticipants, dishes, contents, isExpanded, onToggleExpand, onToggleParticipant, onChangeParticipantPortion, onChangeDishPortion } = this.props;
        const isPrimaryUser = member.uuid == 'USER';

        let portionValue = member.portion;
        let portionText = `${portionValue} ${portionValue == 1 ? "serving" : "servings"}`;
        let showControls = false;
        let hasMultipleDishes = false;
        let dishServings = [];

        if (isPrimaryUser && dishes.length) {
            // Always show collapse for multiple dishes, regardless of equal servings
            if (dishes.length > 1) {
                hasMultipleDishes = true;
                let totalServings = 0;
                dishServings = dishes.map(({main}, index) => {
                    const dishContent = contents[main.recipe_uuid || main.food_uuid];
                    const servingsValue = calculateParticipantServings(member, main, dishContent);
                    totalServings += servingsValue;
                    const dishName = dishContent?.title || dishContent?.name || `Dish ${index + 1}`;
                    return {
                        name: dishName,
                        servings: servingsValue,
                        dishId: main.id || main.uuid,  // Use ID for identity, not index
                        dishNumber: index + 1
                    };
                });

                // Always show individual serving value + dish count
                const firstServingsValue = dishServings[0].servings;
                const allSameServings = dishServings.every(d => d.servings === firstServingsValue);

                portionValue = totalServings;

                if (allSameServings) {
                    // When all dishes have same value, show that value + dish count
                    portionText = `${firstServingsValue} ${firstServingsValue == 1 ? "serving" : "servings"} (${dishes.length} ${dishes.length == 1 ? "dish" : "dishes"})`;
                } else {
                    // When dishes have different values, show total + dish count
                    portionText = `${totalServings} ${totalServings == 1 ? "serving" : "servings"} (${dishes.length} ${dishes.length == 1 ? "dish" : "dishes"})`;
                }
            } else {
                // Single dish - show inline controls
                const dishContent = contents[dishes[0].main.recipe_uuid || dishes[0].main.food_uuid];
                const firstServingsValues = calculateParticipantServings(member, dishes[0].main, dishContent);
                portionValue = firstServingsValues;
                portionText = `${firstServingsValues} ${firstServingsValues == 1 ? "serving" : "servings"}`;
                showControls = true;
            }
        } else {
            // Non-primary family members: no controls, just display their portion
            // Their portion sizes come from profile.family and can't be edited per-meal
            // For multiple dishes, show "per dish" to clarify
            if (dishes.length > 1) {
                portionText = `${portionValue} ${portionValue == 1 ? "serving" : "servings"} per dish`;
            }
            showControls = false;
        }

        return (
            <div className={`participant-row ${hasMultipleDishes ? 'has-multiple-dishes' : ''}`}>
                {hasMultipleDishes ? (
                    <>
                        <div className="participant-main-row">
                            <div className="participant-checkbox-area">
                                <button
                                    className="el-checkbox"
                                    onClick={editableParticipants ? () => onToggleParticipant(member) : null}
                                    data-active={isEating}
                                    data-editable={editableParticipants}
                                    disabled={!editableParticipants}
                                >
                                    <span className="participant-name">{member.name}</span>
                                </button>
                            </div>

                            <div className="participant-serving-area">
                                {!isEating && (
                                    <span className="not-eating">Not eating</span>
                                )}
                            </div>
                        </div>

                        {isEating && (
                            <div className="dishes-breakdown">
                                {dishServings.map(({name, servings, dishId, dishNumber}, idx) => (
                                    <div key={idx} className="dish-serving-row">
                                        <span className="dish-name">Dish {dishNumber}: {name}</span>
                                        <div className="serving-controls">
                                            <button
                                                className="serving-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onChangeDishPortion(member, dishId, servings - 0.25);
                                                }}
                                                disabled={servings <= 0.25}
                                            >
                                                -
                                            </button>
                                            <span className="serving-amount">{servings} {servings == 1 ? "serving" : "servings"}</span>
                                            <button
                                                className="serving-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onChangeDishPortion(member, dishId, servings + 0.25);
                                                }}
                                            >
                                                +
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        <div className="participant-checkbox-area">
                            <button
                                className="el-checkbox"
                                onClick={editableParticipants ? () => onToggleParticipant(member) : null}
                                data-active={isEating}
                                data-editable={editableParticipants}
                                disabled={!editableParticipants}
                            >
                                <span className="participant-name">{member.name}</span>
                            </button>
                        </div>

                        <div className="participant-serving-area">
                            {isEating ? (
                                showControls ? (
                                    // Single dish - inline controls
                                    <div className="serving-controls">
                                        <button
                                            className="serving-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onChangeParticipantPortion(member, portionValue - 0.25);
                                            }}
                                            disabled={portionValue <= 0.25}
                                        >
                                            -
                                        </button>
                                        <span className="serving-amount">{portionText}</span>
                                        <button
                                            className="serving-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onChangeParticipantPortion(member, portionValue + 0.25);
                                            }}
                                        >
                                            +
                                        </button>
                                    </div>
                                ) : (
                                    <span className="serving-amount">{portionText}</span>
                                )
                            ) : (
                                <span className="not-eating">Not eating</span>
                            )}
                        </div>
                    </>
                )}
            </div>
        );
    }
}
